import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Ticket, TicketStatus } from '../../entities/ticket';
import { getTicketById } from '../../entities/ticket/api/ticketApi';
import { TicketList } from '../../features/ticket-list';
import { TicketChat } from '../../features/ticket-chat';
import { TicketDetails } from '../../features/ticket-details';
import { CreateTicketModal } from '../../features/ticket-create';
import { ResizablePanel } from '../../shared/components/ResizablePanel';
import * as S from './TicketsPage.styles';

export const TicketsPage: React.FC = () => {
  const autoAdvanceStatuses = new Set<TicketStatus>([
    'closed',
    'resolved',
    'in_programming',
    'spam',
    'duplicate',
  ]);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [visibleTickets, setVisibleTickets] = useState<Ticket[]>([]);
  const [pendingAutoAdvanceFromTicketId, setPendingAutoAdvanceFromTicketId] = useState<string | null>(null);
  const queryTicketId = searchParams.get('ticket');
  
  const loadPanelSizes = () => {
    const savedLeft = localStorage.getItem('tickets_left_panel_width');
    const savedRight = localStorage.getItem('tickets_right_panel_width');
    return {
      left: savedLeft ? parseInt(savedLeft, 10) : 320,
      right: savedRight ? parseInt(savedRight, 10) : 360,
    };
  };
  
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => loadPanelSizes().left);
  const [rightPanelWidth, setRightPanelWidth] = useState(() => loadPanelSizes().right);
  
  const handleLeftPanelResize = (newSize: number) => {
    setLeftPanelWidth(newSize);
    localStorage.setItem('tickets_left_panel_width', newSize.toString());
  };
  
  const handleRightPanelResize = (newSize: number) => {
    setRightPanelWidth(newSize);
    localStorage.setItem('tickets_right_panel_width', newSize.toString());
  };

  const loadTicketById = async (ticketId: string) => {
    try {
      const ticket = await getTicketById(ticketId);
      if (ticket) {
        setSelectedTicket(ticket);
      } else {
        setSelectedTicket(null);
      }
    } catch (error) {
      console.error('Ошибка загрузки тикета:', error);
    }
  };

  useEffect(() => {
    const ticketId = params.id || queryTicketId;
    if (!ticketId) {
      return;
    }
    loadTicketById(ticketId);
    if (!params.id && queryTicketId) {
      setSearchParams({}, { replace: true });
    }
  }, [params.id, queryTicketId, setSearchParams]);

  const handleTicketSelect = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    loadTicketById(ticket.id);
  };

  const handleTicketCreated = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setIsCreateModalOpen(false);
    setRefreshTrigger(prev => prev + 1);
    loadTicketById(ticket.id);
  };

  const handleMessageSent = async () => {
    if (selectedTicket?.id) {
      await loadTicketById(selectedTicket.id);
    }
    setRefreshTrigger(prev => prev + 1);
  };

  const handleVisibleTicketsChange = (nextTickets: Ticket[]) => {
    if (pendingAutoAdvanceFromTicketId) {
      const previousIndex = visibleTickets.findIndex(t => t.id === pendingAutoAdvanceFromTicketId);
      const nextTicket =
        (previousIndex >= 0 && (nextTickets[previousIndex] || nextTickets[previousIndex - 1])) ||
        nextTickets[0] ||
        null;
      setPendingAutoAdvanceFromTicketId(null);
      if (nextTicket) {
        setSelectedTicket(nextTicket);
        loadTicketById(nextTicket.id);
      } else {
        setSelectedTicket(null);
      }
    }
    setVisibleTickets(nextTickets);
  };

  const handleStatusChange = async (newStatus?: TicketStatus) => {
    const shouldAutoAdvance = !!newStatus && autoAdvanceStatuses.has(newStatus);
    if (shouldAutoAdvance && selectedTicket?.id) {
      setPendingAutoAdvanceFromTicketId(selectedTicket.id);
    }
    setRefreshTrigger(prev => prev + 1);
    if (selectedTicket?.id && !shouldAutoAdvance) {
      await loadTicketById(selectedTicket.id);
    }
  };

  return (
    <S.TicketsPageContainer>
      <ResizablePanel
        direction="horizontal"
        initialSize={leftPanelWidth}
        minSize={250}
        maxSize={800}
        onResize={handleLeftPanelResize}
      >
        <S.LeftPanel>
          <TicketList
            selectedTicketId={selectedTicket?.id}
            onTicketSelect={handleTicketSelect}
            onCreateTicket={() => setIsCreateModalOpen(true)}
            refreshTrigger={refreshTrigger}
            onVisibleTicketsChange={handleVisibleTicketsChange}
          />
        </S.LeftPanel>
      </ResizablePanel>

      <S.CenterPanel>
        <TicketChat
          ticket={selectedTicket}
          onMessageSent={handleMessageSent}
          onStatusChange={handleStatusChange}
        />
      </S.CenterPanel>

      <ResizablePanel
        direction="horizontal"
        initialSize={rightPanelWidth}
        minSize={250}
        maxSize={800}
        onResize={handleRightPanelResize}
        $resizeFromLeft={true}
      >
        <S.RightPanel>
          <TicketDetails ticket={selectedTicket} />
        </S.RightPanel>
      </ResizablePanel>

      <CreateTicketModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onTicketCreated={handleTicketCreated}
      />
    </S.TicketsPageContainer>
  );
};
